import { useState, useEffect, useRef } from "react";
import { mockUser } from "@/mocks/crypto";
import { updateKYCProfile, updateProfile } from "../../../api/apiServices";


export default function PersonalInfoModal({ isOpen, onClose, user, onUpdateSuccess }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState({ name: "", email: "", phone: "" });
  const [draft, setDraft] = useState({ name: "", email: "", phone: "" });
  const [previewUrl, setPreviewUrl] = useState(null);

  // IMPORTANT: Sync state when the 'user' prop changes
  useEffect(() => {
    if (user) {
      const userData = {
        name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        email: user.email || "",
        phone: user.phone || "",
        dob: user.dob || "",
        gender: user.gender || "Male",
        address: user.location?.address || "",
        state: user.location?.state || "",
        country: user.location?.country || "Nigeria",
        avatar: user.avatar
      };
      setInfo(userData);
      setDraft(userData);
    }
  }, [user, isOpen]); // Runs when user is fetched or modal opens
  

  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setSaved(false);
      setDraft(info);
    }
  }, [isOpen]);

  const handleSave = async () => {
  setLoading(true);
  setError(null);

  try {
    const formData = new FormData();

    // 1. Split name for the backend schema
    const nameParts = draft.name.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");

    // 2. Append text fields
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("phone", draft.phone);
    formData.append("userName", draft.userName || "");
    formData.append("gender", draft.gender);
    formData.append("dob", draft.dob);

    // 3. Append location (Backend expects nested object or flattened keys)
    // If your backend uses bodyParser.json() for nested, you might need to flatten:
    formData.append("address", draft.address);
    formData.append("state", draft.state);

    // 4. Append the file if a new one was selected
    if (fileInputRef.current?.files[0]) {
      formData.append("avatar", fileInputRef.current.files[0]);
    }

    // 5. API Call
    const response = await updateKYCProfile(formData);

    if (response.status) {
      setInfo({
        ...draft,
        avatar: response.user.avatar // Update UI with the new server URL
      });
      setIsEditing(false);
      setSaved(true);
      if (onUpdateSuccess) onUpdateSuccess(response.user);
      setTimeout(() => setSaved(false), 3000);
    }
  } catch (err) {
    setError(err.response?.data?.message || "Failed to update profile");
  } finally {
    setLoading(false);
  }
};

  const handleCancel = () => {
    setDraft(info);
    setIsEditing(false);
  };

  const fields = [
    { key: "name", label: "Full Name", icon: "ri-user-3-line", type: "text" },
    { key: "email", label: "Email Address", icon: "ri-mail-line", type: "email", editable: false },
    { key: "phone", label: "Phone Number", icon: "ri-phone-line", type: "tel" },
    { key: "dob", label: "Date of Birth", icon: "ri-calendar-line", type: "date" },
    { key: "gender", label: "Gender", icon: "ri-user-heart-line", type: "select" },
    { key: "address", label: "Home Address", icon: "ri-map-pin-line", type: "text" },
    { key: "state", label: "State", icon: "ri-building-line", type: "text" },
    { key: "country", label: "Country", icon: "ri-earth-line", type: "text", editable: false },
  ];


  const fileInputRef = useRef(null);

const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    // Create a temporary local URL for the UI
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }
};



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl w-full max-h-[92vh] flex flex-col"
        style={{ animation: "slideUp 0.3s ease-out" }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 cursor-pointer hover:bg-slate-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-slate-600 text-lg" />
            </button>
            <div>
              <h2
                className="text-slate-800 font-bold text-base"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Personal Information
              </h2>
              <p className="text-slate-400 text-xs">Manage your profile details</p>
            </div>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 text-emerald-600 text-sm font-semibold px-3 py-2 rounded-xl cursor-pointer hover:bg-emerald-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-edit-line text-sm" />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="text-slate-500 text-sm font-medium px-3 py-2 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
              onClick={handleSave}
              disabled={loading}
              className="bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>

        {/* Success toast */}
        {saved && (
          <div className="mx-5 mt-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-600 text-base" />
            <p className="text-emerald-700 text-sm font-medium">Profile updated successfully!</p>
          </div>
        )}

        {/* Avatar section */}
        <div className="flex flex-col items-center py-5 flex-shrink-0">
          <div className="relative">
  <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-emerald-200 bg-emerald-50">
    {previewUrl || info.avatar ? (
      <img 
        src={previewUrl || info.avatar} 
        alt="Profile" 
        className="w-full h-full object-cover" 
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center">
        <span className="text-emerald-700 font-bold text-3xl">
          {info.name.charAt(0)}
        </span>
      </div>
    )}
  </div>

  {isEditing && (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="absolute -bottom-1 -right-1 w-7 h-7 bg-emerald-600 rounded-full flex items-center justify-center cursor-pointer shadow-lg active:scale-90 transition-transform"
      >
        <i className="ri-camera-line text-white text-xs" />
      </button>
    </>
  )}
</div>
          <p
            className="text-slate-800 font-bold text-base mt-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {info.name}
          </p>
          <div className="flex items-center gap-1 mt-1 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
            <i className="ri-shield-check-line text-emerald-600 text-xs" />
            <span className="text-emerald-600 text-xs font-semibold">KYC Verified</span>
          </div>
        </div>

        {/* Fields */}
        <div className="overflow-y-auto flex-1 px-5 pb-8">
          <div className="bg-slate-50 rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {fields.map((field) => {
              const isFieldEditable = field.editable !== false;
              return (
                <div key={field.key} className="flex items-start gap-3 px-4 py-3.5">
                  <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${field.icon} text-slate-400 text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-400 text-xs mb-0.5">{field.label}</p>
                    {isEditing && isFieldEditable ? (
                      field.type === "select" ? (
                        <select
                          value={draft[field.key]}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          className="w-full text-slate-700 text-sm font-medium bg-transparent border-b border-emerald-300 pb-0.5 outline-none cursor-pointer"
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Non-binary">Non-binary</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                      ) : (
                        <input
                          type={field.type || "text"}
                          value={draft[field.key]}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          className="w-full text-slate-700 text-sm font-medium bg-transparent border-b border-emerald-300 pb-0.5 outline-none"
                        />
                      )
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-slate-700 text-sm font-medium truncate">
                          {field.key === "dob"
                            ? new Date(info[field.key]).toLocaleDateString("en-NG", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : info[field.key]}
                        </p>
                        {!isFieldEditable && (
                          <span className="text-slate-300 text-xs bg-slate-100 px-1.5 py-0.5 rounded-md flex-shrink-0">
                            Locked
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-slate-400 text-xs text-center mt-4 px-4">
            Some fields like email and country are locked for security. Contact support to update them.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
